import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as AssistantAPI from 'api/WorkspaceAssistant';
import { unwrap, apiErrorMessage, notify } from 'utils/notify';

// ───────────────────────────────────────────────────────────────────────────
// Workspace-assistant store — mirrors the singleton
// /api/super/workspace-assistant/.
//
// One row, so there is no list/pagination state: `data` is the row as the
// server last returned it, and the page keeps its own draft until Save.
// `loading` covers the GET, `saving` the PATCH and `resetting` the
// reset-prompts POST, so no spinner ever blanks out the form.
// ───────────────────────────────────────────────────────────────────────────

// The two numeric columns must go over the wire as JSON numbers, but MUI
// TextFields hand back strings. Empty string means "clear it" → send null
// rather than NaN.
const NUMERIC_KEYS = ['temperature', 'max_tokens'];

export const toApiPayload = (body) =>
  Object.fromEntries(
    Object.entries(body).map(([k, v]) => {
      if (!NUMERIC_KEYS.includes(k)) return [k, v];
      if (v === '' || v == null) return [k, null];
      const n = Number(v);
      return [k, Number.isNaN(n) ? v : n];
    })
  );

const initialState = {
  data: {},
  loading: false,
  saving: false,
  resetting: false,
  error: null
};

// ── thunks ────────────────────────────────────────────────────────────────

const getAssistantConfig = createAsyncThunk('workspaceAssistant/get', async (signal, { rejectWithValue }) => {
  try {
    const res = await AssistantAPI.getAssistantConfig(signal);
    return unwrap(res) || {};
  } catch (error) {
    // The page aborts its in-flight GET on unmount — not a failure the user
    // needs a snackbar about.
    if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') return rejectWithValue(null);
    const message = apiErrorMessage(error, 'Failed to load assistant configuration.');
    notify.error(message);
    return rejectWithValue(message);
  }
});

// `body` is the changed-keys subset — PATCH is partial, so anything left out
// (typically `api_key`) keeps its stored value.
const updateAssistantConfig = createAsyncThunk('workspaceAssistant/update', async (body, { rejectWithValue }) => {
  try {
    const res = await AssistantAPI.updateAssistantConfig(toApiPayload(body));
    notify.success('Assistant configuration updated.');
    return unwrap(res) || {};
  } catch (error) {
    const message = apiErrorMessage(error);
    notify.error(message);
    return rejectWithValue(message);
  }
});

const resetAssistantPrompts = createAsyncThunk('workspaceAssistant/resetPrompts', async (prompts, { rejectWithValue }) => {
  try {
    const res = await AssistantAPI.resetAssistantPrompts(prompts);
    notify.success('Prompts restored to the shipped defaults.');
    return unwrap(res) || {};
  } catch (error) {
    const message = apiErrorMessage(error);
    notify.error(message);
    return rejectWithValue(message);
  }
});

// ── slice ─────────────────────────────────────────────────────────────────

const WorkspaceAssistantSlice = createSlice({
  name: 'workspaceAssistant',
  initialState,
  reducers: {
    resetState: () => initialState
  },
  extraReducers: {
    [getAssistantConfig.pending]: (state) => {
      state.loading = true;
      state.error = null;
    },
    [getAssistantConfig.fulfilled]: (state, action) => {
      state.loading = false;
      state.data = action.payload || {};
    },
    [getAssistantConfig.rejected]: (state, action) => {
      state.loading = false;
      state.error = action.payload || action.error?.message;
      // Nothing configured yet (or the row was never created) — leave the page
      // with a blank form rather than a permanently empty error state.
      state.data = {};
    },

    [updateAssistantConfig.pending]: (state) => {
      state.saving = true;
      state.error = null;
    },
    [updateAssistantConfig.fulfilled]: (state, action) => {
      state.saving = false;
      // PATCH echoes the full row back; merge so a partial response can't drop
      // fields the form is still showing.
      state.data = { ...state.data, ...(action.payload || {}) };
    },
    [updateAssistantConfig.rejected]: (state, action) => {
      state.saving = false;
      state.error = action.payload || action.error?.message;
    },

    [resetAssistantPrompts.pending]: (state) => {
      state.resetting = true;
      state.error = null;
    },
    [resetAssistantPrompts.fulfilled]: (state, action) => {
      state.resetting = false;
      state.data = { ...state.data, ...(action.payload || {}) };
    },
    [resetAssistantPrompts.rejected]: (state, action) => {
      state.resetting = false;
      state.error = action.payload || action.error?.message;
    }
  }
});

const { resetState } = WorkspaceAssistantSlice.actions;

export default WorkspaceAssistantSlice.reducer;

export { resetState, getAssistantConfig, updateAssistantConfig, resetAssistantPrompts };
